import * as yup from 'yup'
import React from 'react'
import css from './pages.module.css'
import { familyRelationship, suffixes } from '../choices'
import { Divider } from '../components/divider'

import {
  addressLabel,
  address2Label,
  aliasLabel,
  cellPhoneLabel,
  dobLabel,
  employerLabel,
  employerPhoneLabel,
  firstNameLabel,
  homeAddressLabel,
  homePhoneLabel,
  jobTitleLabel,
  lastNameLabel,
  middleNameLabel,
  optionalLabel,
  otherRelationshipLabel,
  personalEmailLabel,
  physicalDescLabel,
  suffixLabel,
  workEmailLabel,
  workPhoneLabel
} from '../strings/labels'
import {
  addressLengthMessage,
  cellPhoneMessage,
  cityLengthMessage,
  employerPhoneMessage,
  firstNameMessage,
  firstNameLengthMessage,
  genericMessage,
  homePhoneMessage,
  jobTitleLengthMessage,
  lastNameLengthMessage,
  lastNameMessage,
  makeSelectionMessage,
  maxLength150Message,
  maxLength500Message,
  middleNameLengthMessage,
  middleNameMessage,
  otherLengthMessage,
  personalEmailLengthMessage,
  personalEmailMessage,
  workEmailLengthMessage,
  workEmailMessage,
  workPhoneMessage,
  zipMessage,
  zipLengthMessage
} from '../strings/messages'
import { alphaRegex, phoneRegex, zipRegex } from '../strings/regex'
import { subjectType } from '../strings/types'
import { dateYupOptional } from '../validators/dateYup'

const SubjectDetailsHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Subject</h2>
)

const EmploymentInformationHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Employment information</h2>
)

const ContactInformationHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Subject contact information</h2>
)

const HomeAddressHeading = () => (
  <h2 className="ds-u-margin-bottom--0">{homeAddressLabel}</h2>
)

const RelationshipHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Your relationship to subject</h2>
)

export const subjectInfoIndividualPage = () => ({
  id: 'subject_info_individual_page',
  title: 'Subject information',
  description:
    'Please provide contact information that will help us identify the person responsible for the fraudulent action(s).',
  validation: yup.object().shape({
    subj_first_name_0: yup
      .string()
      .matches(alphaRegex, {
        message: firstNameMessage,
        excludeEmptyString: false
      })
      .max(20, firstNameLengthMessage)
      .required(firstNameMessage),
    subj_middle_name_0: yup
      .string()
      .matches(alphaRegex, {
        message: middleNameMessage,
        excludeEmptyString: false
      })
      .max(20, middleNameLengthMessage),
    subj_last_name_0: yup
      .string()
      .matches(alphaRegex, {
        message: lastNameMessage,
        excludeEmptyString: false
      })
      .max(30, lastNameLengthMessage)
      .required(lastNameMessage),
    subj_home_phone_0: yup.string().matches(phoneRegex, {
      message: homePhoneMessage,
      excludeEmptyString: false
    }),
    subj_cell_phone_0: yup.string().matches(phoneRegex, {
      message: cellPhoneMessage,
      excludeEmptyString: false
    }),
    subj_work_phone_0: yup.string().matches(phoneRegex, {
      message: workPhoneMessage,
      excludeEmptyString: false
    }),
    subj_employer_phone_0: yup.string().matches(phoneRegex, {
      message: employerPhoneMessage,
      excludeEmptyString: false
    }),
    subj_job_title_0: yup.string().max(30, jobTitleLengthMessage),
    subj_personal_email_0: yup
      .string()
      .email(personalEmailMessage)
      .max(250, personalEmailLengthMessage),
    subj_work_email_0: yup
      .string()
      .email(workEmailMessage)
      .max(250, workEmailLengthMessage),
    subj_address_0: yup.object().shape({
      line1Value: yup.string().max(50, addressLengthMessage),
      line2Value: yup.string().max(50, addressLengthMessage),
      cityValue: yup.string().max(30, cityLengthMessage),
      zipValue: yup
        .string()
        .max(10, zipLengthMessage)
        .test('zip_correct_format', zipMessage, function(value) {
          const { countryValue } = this.parent
          return countryValue === 'US'
            ? value === '' || zipRegex.test(value)
            : true
        })
    }),
    subj_employer_0: yup.string().max(150, maxLength150Message),
    subj_employer_address_0: yup.object().shape({
      line1Value: yup.string().max(50, addressLengthMessage),
      line2Value: yup.string().max(50, addressLengthMessage),
      cityValue: yup.string().max(30, cityLengthMessage),
      zipValue: yup
        .string()
        .max(10, zipLengthMessage)
        .test('zip_correct_format', zipMessage, function(value) {
          const { countryValue } = this.parent
          return countryValue === 'US'
            ? value === '' || zipRegex.test(value)
            : true
        })
    }),
    subj_dob_0: dateYupOptional(),
    subj_relationship_0: yup.string().required(makeSelectionMessage),
    subj_relationship_other_0: yup
      .string()
      .max(150, otherLengthMessage)
      .test('subj_rel_other_req', genericMessage, function(value) {
        const { subj_relationship_0 } = this.parent
        if (subj_relationship_0 && subj_relationship_0.includes('Other'))
          return value != null
        return true
      }),
    subj_alias_nickname_0: yup.string().max(150, maxLength150Message),
    subj_physical_description_0: yup.string().max(500, maxLength500Message)
  }),
  fields: [
    {
      type: 'content',
      content: SubjectDetailsHeading,
      name: 'subject_details'
    },
    {
      type: 'text',
      label: 'Known subject',
      name: 'known_subject',
      value: 'Yes',
      className: css.hidden
    },
    {
      type: 'text',
      label: 'Person',
      name: 'subject_type_0',
      value: subjectType.singleIndividual,
      className: css.hidden
    },
    {
      type: 'text',
      label: 'Single subject',
      name: 'single_subject_0',
      value: 'Yes',
      className: css.hidden
    },
    {
      type: 'text',
      label: firstNameLabel,
      name: 'subj_first_name_0'
    },
    {
      type: 'text',
      label: middleNameLabel,
      name: 'subj_middle_name_0',
      requirementLabel: optionalLabel
    },
    {
      type: 'text',
      label: lastNameLabel,
      name: 'subj_last_name_0'
    },
    {
      type: 'select',
      label: suffixLabel,
      name: 'subj_suffix_0',
      choices: [
        {
          value: '',
          label: ''
        },
        ...suffixes()
      ],
      requirementLabel: optionalLabel
    },
    {
      type: 'content',
      content: Divider,
      name: 'divider1'
    },
    {
      type: 'text',
      label: aliasLabel,
      name: 'subj_alias_nickname_0',
      requirementLabel: optionalLabel
    },
    {
      type: 'date',
      label: dobLabel,
      name: 'subj_dob_0',
      requirementLabel: optionalLabel,
      overrideDefaultMinMax: true
    },
    {
      type: 'text',
      label: physicalDescLabel,
      name: 'subj_physical_description_0',
      requirementLabel: optionalLabel,
      multiline: true,
      rows: 4
    },
    {
      type: 'content',
      content: Divider,
      name: 'divider2'
    },
    {
      type: 'content',
      content: RelationshipHeading,
      name: 'relationship'
    },
    {
      type: 'radio',
      className: css.choices,
      label: '',
      name: 'subj_relationship_0',
      choices: familyRelationship(),
      conditionals: [
        {
          value: 'Other',
          fields: [
            {
              type: 'text',
              label: otherRelationshipLabel,
              name: 'subj_relationship_other_0',
              autoFocus: true
            }
          ]
        }
      ]
    },
    {
      type: 'content',
      content: Divider,
      name: 'divider3'
    },
    {
      type: 'content',
      content: ContactInformationHeading,
      name: 'contact_information'
    },
    {
      type: 'text',
      label: homePhoneLabel,
      name: 'subj_home_phone_0',
      requirementLabel: optionalLabel
    },
    {
      type: 'text',
      label: cellPhoneLabel,
      name: 'subj_cell_phone_0',
      requirementLabel: optionalLabel
    },
    {
      type: 'text',
      label: workPhoneLabel,
      name: 'subj_work_phone_0',
      requirementLabel: optionalLabel
    },
    {
      type: 'text',
      label: personalEmailLabel,
      name: 'subj_personal_email_0',
      requirementLabel: optionalLabel
    },
    {
      type: 'text',
      label: workEmailLabel,
      name: 'subj_work_email_0',
      requirementLabel: optionalLabel
    },
    {
      type: 'content',
      content: Divider,
      name: 'divider4'
    },
    {
      type: 'content',
      content: HomeAddressHeading,
      name: 'mailing_address'
    },
    {
      type: 'address',
      name: 'subj_address_0',
      label: '',
      hint: '',
      line1Label: addressLabel,
      line2Label: address2Label,
      line1RequirementLabel: optionalLabel,
      line2RequirementLabel: optionalLabel,
      cityRequirementLabel: optionalLabel,
      stateRequirementLabel: optionalLabel,
      zipRequirementLabel: optionalLabel,
      countryValue: 'US'
    },
    {
      type: 'content',
      content: Divider,
      name: 'divider5'
    },
    {
      type: 'content',
      content: EmploymentInformationHeading,
      name: 'employment_information'
    },
    {
      type: 'text',
      label: jobTitleLabel,
      name: 'subj_job_title_0',
      requirementLabel: optionalLabel
    },
    {
      type: 'text',
      label: employerLabel,
      name: 'subj_employer_0',
      requirementLabel: optionalLabel
    },
    {
      type: 'address',
      name: 'subj_employer_address_0',
      label: '',
      hint: '',
      line1Label: addressLabel,
      line2Label: address2Label,
      line1RequirementLabel: optionalLabel,
      line2RequirementLabel: optionalLabel,
      cityRequirementLabel: optionalLabel,
      stateRequirementLabel: optionalLabel,
      zipRequirementLabel: optionalLabel,
      countryValue: 'US'
    },
    {
      type: 'text',
      label: employerPhoneLabel,
      name: 'subj_employer_phone_0',
      requirementLabel: optionalLabel
    }
  ]
})
